import React from 'react';
import { ExamRoomContent } from 'components/organisms/ExamRoom/ExamRoomContent';
import Loader from 'components/atoms/Loader/Loader';
import MDBox from 'components/atoms/MDBox/MDBox';
import MDTypography from 'components/atoms/MDTypography/MDTypography';
import DemoFlowLayout from 'components/templates/DemoFlowLayout/DemoFlowLayout';
import { FullJoints, Xray } from 'models/XrayModels';
import { Patient } from 'models/PatientModels';
import { User } from 'models/UserModels';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { ProductVersion, Roles } from 'models/OrgModels';
import { DemoDataResponse, useDemoExamRoomData } from 'hooks/useDemoExamRoomData';
import { useSearchParams } from 'react-router-dom';

interface IDemoExamRoomContentProps {
  data: DemoDataResponse;
}

const DemoExamRoomContent: React.FC<IDemoExamRoomContentProps> = ({ data }) => {
  // make the user
  const user: User = {
    details: {
      sub: "demo-user-1",
      name: "Demo User",
      email: "demo@example.com",
      email_verified: true,
    },
    cognitoData: {} as CognitoUser,
    orgs: [
      {
        org_id: "org-1",
        org_role_id: "role-1",
        role: Roles.ADMIN,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_org_subscribed: true,
        product_version: ProductVersion.FULL,
        mp_id: "demo-user-1",
      },
    ],
    can: (permission) => true,
  };

  // make the active patent
  const activePatient: Patient = {
    patient_id: data.patient.patient_id,
    first_name: data.patient.first_name,
    last_name: data.patient.last_name,
    date_of_birth: data.patient.patient_id,
    external_id: data.patient.patient_id,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    org_id: "",
  };

  const {
    appointment,
    xraysMappedToJoints,
    latestProsMappedToJoints,
    roiImgUrls,
    appointmentRequiredJointViews,
  } = data;

  const mockBatchConfirmXrays = async (
    xrays: Xray[],
    fullJoint?: FullJoints
  ) => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return;
  };

  return (
    <DemoFlowLayout title="Exam Room" singlePatient={activePatient}>
      <ExamRoomContent
        user={user}
        appointment={appointment}
        xraysMappedToJoints={xraysMappedToJoints}
        latestProsMappedToJoints={latestProsMappedToJoints}
        roiImgUrls={roiImgUrls}
        appointmentRequiredJointViews={appointmentRequiredJointViews}
        batchConfirmXrays={mockBatchConfirmXrays}
        activePatient={activePatient}
        flow="DEMO"
        initalQueriesLoading={false}
        updateXrayLoading={false}
        roiImagesLoading={false}
      />
    </DemoFlowLayout>
  );
};

const DemoExamRoomFallback: React.FC = () => {
  return (
    <MDBox
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
    >
      <MDTypography variant="h4" mb={3}>Loading Demo Exam Room</MDTypography>
      <Loader />
    </MDBox>
  );
}

const DemoExamRoom: React.FC = () => {
  const [searchParams] = useSearchParams()
  const patientName = searchParams.get('patientName')

  const demoData = useDemoExamRoomData(patientName);

  if (!demoData.data || demoData.isLoading || demoData.error) {
    return (
      <DemoExamRoomFallback />
    );
  } else {
    return <DemoExamRoomContent data={demoData.data} />
  }
}

export default DemoExamRoom; 